import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { ACPReminder } from "@/features/dashboard/components/reminder-banner/ACPReminder";
import { PaywallReminder } from "@/features/dashboard/components/reminder-banner/PaywallReminder";
import { redirectOnSignIn } from "@/utils/redirectOnSignIn";
import { ReminderContainer } from "@/features/dashboard/components/reminder-banner/ReminderContainer";
import { selectDaysUntilPasswordReset, selectProfile } from "@/redux/selectors/authSelectors";
import { selectScheduleList } from "@/redux/selectors/patientSelectors";
import { selectShowPaywall } from "@/redux/selectors/patientCardInfoSelectors";
import { showAlertWithActionAndCustomButton } from "@/redux/actions/alerts";
import { updateScheduleList } from "@/redux/actions/patient";
import { useACPStatus } from "@/features/acp/hooks/useACPStatus";
import { useSendBird } from "@/features/messaging/hooks/useSendBird";
import { ViewContent2 } from "@/components/layout/ViewContent";
import { ViewTitle } from "@/features/dashboard/components/ViewTitle";

import { AccomplishmentsWidget } from "./components/Widget/AccomplishmentsWidget";
import { CareTeamWidget } from "./components/Widget/CareTeamWidget";
import { FamilyMembersWidget } from "./components/Widget/FamilyMembersWidget";
import { HCPAutoCancelModal } from "./components/HCPAutoCancelModal";
import { HCPDIReminderModal } from "./components/HCPDIReminderModal";
import { InactiveModal } from "./components/InactiveModal";
import { InstantCall } from "./components/InstantCall";
import { isInactive, shouldShowFamilyMembersWidget } from "./helpers/misc";
import { MessagesWidget } from "./components/Widget/MessagesWidget";
import { NextSessionWidget } from "./components/Widget/NextSessionWidget";
import { NextStepWidget } from "./components/Widget/NextStepWidget";
import { ScheduleWidget } from "./components/Widget/ScheduleWidget";
import { TasksWidget } from "./components/Widget/TasksWidget";
import { WaitListModal } from "./components/WaitListModal";

import "./styles.scss";

export function PatientHome() {
  const dispatch = useDispatch();
  const history = useHistory();

  const daysUntilPasswordReset = useSelector(selectDaysUntilPasswordReset);
  const profile = useSelector(selectProfile);
  const scheduleList = useSelector(selectScheduleList);

  const isPaymentReminderShown = useSelector(selectShowPaywall);
  const isAcpReminderShown = useACPStatus() === "NOT_RESPONDED";

  const { sendBirdContacts } = useSendBird({ profile });

  useEffect(() => {
    redirectOnSignIn(history);
    dispatch(updateScheduleList());
  }, []);

  useEffect(() => {
    if (!(daysUntilPasswordReset == null)) {
      if (daysUntilPasswordReset <= 7) {
        dispatch(
          showAlertWithActionAndCustomButton(
            `For security best practices and your protection aptihealth requires you to change your password every 90 days. Your password is set to expire in ${daysUntilPasswordReset} days.`,
            () => {
              history.push("/app/change-password");
            },
            "Change Password",
          ),
        );
      }
    }
  }, [daysUntilPasswordReset]);

  const firstName = profile.preferred_name || profile.first_name;

  return (
    <ViewContent2 data-public>
      <InactiveModal />
      <WaitListModal />
      <HCPDIReminderModal />
      <HCPAutoCancelModal />

      <ReminderContainer>
        {scheduleList && <InstantCall calls={scheduleList} />}
        {isPaymentReminderShown && <PaywallReminder />}
        {isAcpReminderShown && <ACPReminder />}
      </ReminderContainer>

      <ViewTitle data-private>{firstName ? `Welcome, ${firstName}!` : ""}</ViewTitle>

      {profile && !isInactive(profile) && <NextStepWidget />}

      {profile &&
        (profile.schedule_video_call_date_time || profile.ninety_day_care !== "not_assigned") && (
          <section className="tw:grid tw:gap-5 tw:md:grid-cols-3">
            <NextSessionWidget scheduleList={scheduleList} />
            <TasksWidget profile={profile} />

            <MessagesWidget profile={profile} sendBirdContacts={sendBirdContacts} />
          </section>
        )}

      <section className="tw:grid tw:gap-5 tw:md:grid-cols-2">
        <ScheduleWidget profile={profile} scheduleList={scheduleList} />
        <AccomplishmentsWidget profile={profile} />
      </section>

      {profile && shouldShowFamilyMembersWidget(profile) && (
        <FamilyMembersWidget profile={profile} />
      )}

      <CareTeamWidget profile={profile} sendBirdContacts={sendBirdContacts} />
    </ViewContent2>
  );
}
